import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XCircleIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { Card, CardBody, Spinner, Button, Badge } from '@/components/ui';
import { paymentLinkApi, paymentApi, getErrorMessage } from '@/lib/api';
import type { PaymentLink, PaymentLinkItem, RecurringSchedule } from '@/types';

declare global {
  interface Window {
    AdyenCheckout: (config: AdyenCheckoutConfig) => Promise<AdyenCheckoutInstance>;
  }
}

interface AdyenCheckoutConfig {
  environment: string;
  clientKey: string;
  session: {
    id: string;
    sessionData: string;
  };
  onPaymentCompleted: (result: { resultCode: string }) => void;
  onError: (error: Error) => void;
  paymentMethodsConfiguration?: {
    card?: {
      hasHolderName: boolean;
      holderNameRequired: boolean;
    };
  };
}

interface AdyenCheckoutInstance {
  create: (type: string) => { mount: (selector: string) => void };
}

// Extended PaymentLink type for the response with line items and schedules
interface PaymentLinkWithDetails extends PaymentLink {
  lineItems?: PaymentLinkItem[];
  recurringSchedules?: RecurringSchedule[];
}

export function PaymentPage() {
  const { linkCode } = useParams<{ linkCode: string }>();
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dropinLoading, setDropinLoading] = useState(false);
  const [dropinError, setDropinError] = useState<string | null>(null);
  const [dropinMounted, setDropinMounted] = useState(false);

  // Fetch payment link
  const { data: linkData, isLoading: linkLoading, error: linkError } = useQuery({
    queryKey: ['payment-link', linkCode],
    queryFn: async () => {
      const response = await paymentLinkApi.getByLinkCode(linkCode!);
      return response.data.data as PaymentLinkWithDetails;
    },
    enabled: !!linkCode,
  });

  // Fetch Adyen config
  const { data: adyenConfig } = useQuery({
    queryKey: ['adyen-config'],
    queryFn: async () => {
      const response = await paymentApi.getConfig();
      return response.data.data as { clientKey: string; environment: string };
    },
  });

  // Create payment session
  const { data: sessionData, isLoading: sessionLoading } = useQuery({
    queryKey: ['payment-session', linkCode],
    queryFn: async () => {
      const response = await paymentApi.createSession(linkCode!);
      return response.data.data as {
        sessionId: string;
        sessionData: string;
        amount: number;
        currency: string;
      };
    },
    enabled: !!linkCode && (linkData?.status === 'ACTIVE' || linkData?.status === 'PARTIALLY_PAID'),
  });

  // Initialize Adyen Drop-in
  useEffect(() => {
    if (!adyenConfig || !sessionData || paymentStatus || dropinMounted) {
      return;
    }

    let mounted = true;
    setDropinLoading(true);
    setDropinError(null);

    const loadAdyen = async () => {
      try {
        // Load Adyen CSS
        if (!document.querySelector('link[href*="adyen"]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = `https://checkoutshopper-${adyenConfig.environment}.adyen.com/checkoutshopper/sdk/5.64.0/adyen.css`;
          document.head.appendChild(link);
        }

        // Load Adyen JS
        if (!window.AdyenCheckout) {
          console.log('Loading Adyen SDK...');
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `https://checkoutshopper-${adyenConfig.environment}.adyen.com/checkoutshopper/sdk/5.64.0/adyen.js`;
            script.async = true;
            script.onload = () => {
              console.log('Adyen SDK loaded');
              resolve();
            };
            script.onerror = () => reject(new Error('Failed to load Adyen SDK'));
            document.body.appendChild(script);
          });
        }

        if (!mounted) return;

        console.log('Initializing Adyen Drop-in with config:', {
          environment: adyenConfig.environment,
          clientKey: adyenConfig.clientKey?.substring(0, 10) + '...',
          sessionId: sessionData.sessionId,
        });

        const configuration = {
          environment: adyenConfig.environment,
          clientKey: adyenConfig.clientKey,
          session: {
            id: sessionData.sessionId,
            sessionData: sessionData.sessionData,
          },
          onPaymentCompleted: async (result: { resultCode: string }) => {
            console.log('Payment completed:', result);
            if (result.resultCode === 'Authorised') {
              try {
                // Confirm payment with backend to update status
                await paymentApi.confirmPayment(
                  linkCode!,
                  result.resultCode,
                  sessionData.sessionId
                );
                console.log('Payment confirmed with backend');
              } catch (err) {
                console.error('Failed to confirm payment with backend:', err);
                // Still show success to user since payment was authorized
              }
              setPaymentStatus('success');
            } else {
              setPaymentStatus('error');
              setErrorMessage(`Payment ${result.resultCode.toLowerCase()}`);
            }
          },
          onError: (error: Error) => {
            console.error('Adyen payment error:', error);
            setPaymentStatus('error');
            setErrorMessage(getErrorMessage(error));
          },
          paymentMethodsConfiguration: {
            card: {
              hasHolderName: true,
              holderNameRequired: true,
            },
          },
        };

        const checkout = await window.AdyenCheckout(configuration);
        console.log('Adyen checkout created');

        if (mounted) {
          const container = document.getElementById('dropin-container');
          if (container) {
            const dropin = checkout.create('dropin');
            dropin.mount('#dropin-container');
            console.log('Adyen dropin mounted to container');
            setDropinMounted(true);
            setDropinLoading(false);
          } else {
            console.error('Dropin container not found');
            setDropinError('Payment container not ready. Please refresh the page.');
            setDropinLoading(false);
          }
        }
      } catch (error) {
        console.error('Adyen initialization error:', error);
        if (mounted) {
          setDropinLoading(false);
          setDropinError(getErrorMessage(error));
        }
      }
    };

    loadAdyen();

    return () => {
      mounted = false;
    };
  }, [adyenConfig, sessionData, paymentStatus, dropinMounted]);

  const formatCurrency = (amount: number | string, currency: string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency,
    }).format(numAmount);
  };

  const getScheduleStatusBadge = (status: string, dueDate: string) => {
    const isPastDue = new Date(dueDate) < new Date() && status === 'ACTIVE';

    if (status === 'PAID') {
      return <Badge variant="success">Paid</Badge>;
    }
    if (isPastDue) {
      return <Badge variant="error">Overdue</Badge>;
    }
    if (status === 'ACTIVE') {
      return <Badge variant="warning">Pending</Badge>;
    }
    return <Badge variant="gray">{status}</Badge>;
  };

  if (linkLoading || sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (linkError || !linkData) {
    return (
      <Card>
        <CardBody className="text-center py-12">
          <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircleIcon className="w-8 h-8 text-error" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Payment Link Not Found
          </h2>
          <p className="text-gray-500">
            This payment link doesn't exist or may have been removed.
          </p>
        </CardBody>
      </Card>
    );
  }

  // Check if link is expired
  if (linkData.status === 'EXPIRED') {
    return (
      <Card>
        <CardBody className="text-center py-12">
          <div className="w-16 h-16 bg-warning-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ClockIcon className="w-8 h-8 text-warning" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Payment Link Expired
          </h2>
          <p className="text-gray-500">
            This payment link has expired. Please contact Hotel101 for a new link.
          </p>
        </CardBody>
      </Card>
    );
  }

  // Check if already paid
  if (linkData.status === 'PAID') {
    return (
      <Card>
        <CardBody className="text-center py-12">
          <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-8 h-8 text-success" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Payment Complete
          </h2>
          <p className="text-gray-500 mb-4">
            This payment has already been completed. Thank you!
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left max-w-sm mx-auto">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-gray-500">Amount Paid:</span>
              <span className="font-medium text-right">
                {formatCurrency(linkData.amountPaid, linkData.currency)}
              </span>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Check if cancelled
  if (linkData.status === 'CANCELLED') {
    return (
      <Card>
        <CardBody className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircleIcon className="w-8 h-8 text-gray-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Payment Cancelled
          </h2>
          <p className="text-gray-500">
            This payment link has been cancelled. Please contact Hotel101 for assistance.
          </p>
        </CardBody>
      </Card>
    );
  }

  // Payment success state
  if (paymentStatus === 'success') {
    return (
      <Card>
        <CardBody className="text-center py-12">
          <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-8 h-8 text-success" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Payment Successful!
          </h2>
          <p className="text-gray-500 mb-6">
            Your payment has been processed successfully. A confirmation email will be sent shortly.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left max-w-sm mx-auto">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Amount:</span>
                <span className="font-medium">
                  {formatCurrency(sessionData?.amount || linkData.amount, linkData.currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Reference:</span>
                <span className="font-medium">{linkData.linkCode}</span>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Payment error state
  if (paymentStatus === 'error') {
    return (
      <Card>
        <CardBody className="text-center py-12">
          <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationTriangleIcon className="w-8 h-8 text-error" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Payment Failed
          </h2>
          <p className="text-gray-500 mb-6">
            {errorMessage || 'There was an issue processing your payment. Please try again.'}
          </p>
          <Button onClick={() => setPaymentStatus(null)}>
            Try Again
          </Button>
        </CardBody>
      </Card>
    );
  }

  // Helper to convert Decimal strings to numbers
  const toNumber = (val: number | string): number =>
    typeof val === 'string' ? parseFloat(val) : val;

  const hasLineItems = linkData.lineItems && linkData.lineItems.length > 0;
  const hasRecurringSchedules = linkData.recurringSchedules && linkData.recurringSchedules.length > 0;

  // Calculate amounts based on recurring schedules
  const now = new Date();
  const overdueSchedules = hasRecurringSchedules
    ? linkData.recurringSchedules!.filter(s => s.status !== 'PAID' && new Date(s.dueDate) < now)
    : [];
  const upcomingSchedules = hasRecurringSchedules
    ? linkData.recurringSchedules!.filter(s => s.status !== 'PAID' && new Date(s.dueDate) >= now)
    : [];
  const paidSchedules = hasRecurringSchedules
    ? linkData.recurringSchedules!.filter(s => s.status === 'PAID')
    : [];

  // For recurring payments, amount due is the total of overdue unpaid schedules
  // For non-recurring, it's the remaining amount
  const overdueAmount = overdueSchedules.reduce((sum, s) => sum + toNumber(s.amount), 0);
  const totalScheduleAmount = hasRecurringSchedules
    ? linkData.recurringSchedules!.reduce((sum, s) => sum + toNumber(s.amount), 0)
    : 0;
  const paidAmount = paidSchedules.reduce((sum, s) => sum + toNumber(s.amount), 0);

  // Amount due: for recurring = overdue unpaid, for non-recurring = amount - amountPaid
  const amountDue = hasRecurringSchedules
    ? overdueAmount
    : toNumber(linkData.amount) - toNumber(linkData.amountPaid);

  return (
    <div className="space-y-6">
      {/* Payment summary */}
      <Card>
        <CardBody>
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{linkData.title}</h1>
              {linkData.description && (
                <p className="text-sm text-gray-500 mt-1">{linkData.description}</p>
              )}
            </div>
            <div className="flex gap-2">
              {linkData.isRecurring && (
                <Badge variant="info">Recurring</Badge>
              )}
              {linkData.status === 'PARTIALLY_PAID' && (
                <Badge variant="warning">Partial Payment</Badge>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            {linkData.customerName && (
              <>
                <span className="text-gray-500">Customer</span>
                <span className="font-medium text-right">{linkData.customerName}</span>
              </>
            )}
            {linkData.propertyLocation && (
              <>
                <span className="text-gray-500">Property</span>
                <span className="font-medium text-right">
                  Hotel101 {linkData.propertyLocation}
                </span>
              </>
            )}
            {linkData.unitNumber && (
              <>
                <span className="text-gray-500">Unit</span>
                <span className="font-medium text-right">{linkData.unitNumber}</span>
              </>
            )}
            <span className="text-gray-500">Reference</span>
            <span className="font-mono text-right">{linkData.linkCode}</span>
            {linkData.expiresAt && (
              <>
                <span className="text-gray-500">Valid Until</span>
                <span className="text-right">
                  {format(new Date(linkData.expiresAt), 'MMM d, yyyy')}
                </span>
              </>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Line Items Breakdown - only show for non-recurring payments */}
      {hasLineItems && !hasRecurringSchedules && (
        <Card>
          <CardBody>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Payment Breakdown
            </h2>
            <div className="divide-y divide-gray-100">
              {linkData.lineItems!.map((item) => (
                <div key={item.id} className="py-3 flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.description}</p>
                    <div className="flex gap-4 text-sm text-gray-500 mt-1">
                      {item.referenceNumber && (
                        <span>Ref: {item.referenceNumber}</span>
                      )}
                      {item.referenceDate && (
                        <span>Due: {format(new Date(item.referenceDate), 'MMM d, yyyy')}</span>
                      )}
                      {item.quantity > 1 && (
                        <span>Qty: {item.quantity}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      {formatCurrency(item.amount, linkData.currency)}
                    </p>
                    {item.quantity > 1 && (
                      <p className="text-xs text-gray-500">
                        {formatCurrency(item.unitPrice, linkData.currency)} each
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-200 mt-4 pt-4">
              <div className="flex justify-between text-lg font-bold">
                <span>Subtotal</span>
                <span>{formatCurrency(linkData.amount, linkData.currency)}</span>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Recurring Payment Schedule */}
      {hasRecurringSchedules && (
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 mb-4">
              <CalendarDaysIcon className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-gray-900">
                Payment Schedule
              </h2>
            </div>

            {/* What you're paying for */}
            {hasLineItems && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">What you're paying for:</h3>
                <div className="space-y-2">
                  {linkData.lineItems!.map((item) => {
                    const itemWithRecurring = item as PaymentLinkItem & {
                      isRecurring?: boolean;
                      recurringFrequency?: string;
                      recurringCount?: number;
                    };
                    return (
                      <div key={item.id} className="flex justify-between items-start text-sm">
                        <div className="flex-1">
                          <span className="text-gray-600">
                            {item.description}
                            {item.quantity > 1 && <span className="text-gray-400"> (x{item.quantity})</span>}
                          </span>
                          {itemWithRecurring.isRecurring && (
                            <div className="flex gap-1 mt-1">
                              <Badge variant="info" className="text-xs">
                                {itemWithRecurring.recurringFrequency?.toLowerCase()}
                              </Badge>
                              {itemWithRecurring.recurringCount && (
                                <span className="text-xs text-gray-400">
                                  {itemWithRecurring.recurringCount} payments
                                </span>
                              )}
                            </div>
                          )}
                          {!itemWithRecurring.isRecurring && (
                            <div className="mt-1">
                              <Badge variant="gray" className="text-xs">one-time</Badge>
                            </div>
                          )}
                        </div>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(item.amount, linkData.currency)}
                          {itemWithRecurring.isRecurring && (
                            <span className="text-xs text-gray-400 block text-right">/payment</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="text-sm text-gray-500 mb-4">
              This is a {linkData.recurringFrequency?.toLowerCase()} recurring payment with {linkData.recurringSchedules!.length} scheduled payments.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 font-medium text-gray-500">#</th>
                    <th className="text-left py-2 font-medium text-gray-500">Due Date</th>
                    <th className="text-right py-2 font-medium text-gray-500">Amount</th>
                    <th className="text-right py-2 font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {linkData.recurringSchedules!.map((schedule) => (
                    <tr key={schedule.id} className="border-b border-gray-100">
                      <td className="py-3 text-gray-600">{schedule.scheduleNumber}</td>
                      <td className="py-3">
                        {format(new Date(schedule.dueDate), 'MMM d, yyyy')}
                      </td>
                      <td className="py-3 text-right font-medium">
                        {formatCurrency(schedule.amount, linkData.currency)}
                      </td>
                      <td className="py-3 text-right">
                        {getScheduleStatusBadge(schedule.status, schedule.dueDate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200">
                    <td colSpan={2} className="py-3 font-semibold">Total</td>
                    <td className="py-3 text-right font-bold">
                      {formatCurrency(
                        linkData.recurringSchedules!.reduce((sum, s) => sum + toNumber(s.amount), 0),
                        linkData.currency
                      )}
                    </td>
                    <td className="py-3 text-right text-sm text-gray-500">
                      {linkData.recurringSchedules!.filter(s => s.status === 'PAID').length} of {linkData.recurringSchedules!.length} paid
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Amount Summary */}
      <Card>
        <CardBody>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Payment Summary
          </h2>
          <div className="space-y-2">
            {/* Non-recurring payment summary */}
            {!hasRecurringSchedules && (
              <>
                {!hasLineItems && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total Amount</span>
                    <span>{formatCurrency(linkData.amount, linkData.currency)}</span>
                  </div>
                )}
                {hasLineItems && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Subtotal ({linkData.lineItems!.length} items)</span>
                    <span>{formatCurrency(linkData.amount, linkData.currency)}</span>
                  </div>
                )}
                {toNumber(linkData.amountPaid) > 0 && (
                  <div className="flex justify-between text-sm text-success">
                    <span>Amount Paid</span>
                    <span>-{formatCurrency(linkData.amountPaid, linkData.currency)}</span>
                  </div>
                )}
              </>
            )}

            {/* Recurring payment summary */}
            {hasRecurringSchedules && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Total Contract Value ({linkData.recurringSchedules!.length} payments)</span>
                  <span>{formatCurrency(totalScheduleAmount, linkData.currency)}</span>
                </div>
                {paidAmount > 0 && (
                  <div className="flex justify-between text-sm text-success">
                    <span>Paid ({paidSchedules.length} payments)</span>
                    <span>-{formatCurrency(paidAmount, linkData.currency)}</span>
                  </div>
                )}
                {overdueSchedules.length > 0 && (
                  <div className="flex justify-between text-sm text-error font-medium">
                    <span>Overdue ({overdueSchedules.length} payments)</span>
                    <span>{formatCurrency(overdueAmount, linkData.currency)}</span>
                  </div>
                )}
                {upcomingSchedules.length > 0 && (
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>Upcoming ({upcomingSchedules.length} payments)</span>
                    <span>{formatCurrency(upcomingSchedules.reduce((sum, s) => sum + toNumber(s.amount), 0), linkData.currency)}</span>
                  </div>
                )}
              </>
            )}

            <div className="border-t border-gray-200 pt-3 mt-3">
              <div className="flex justify-between text-xl font-bold">
                <span>{hasRecurringSchedules && overdueSchedules.length > 0 ? 'Amount Due Now' : 'Amount Due'}</span>
                <span className={overdueSchedules.length > 0 ? 'text-error' : 'text-primary'}>
                  {formatCurrency(amountDue, linkData.currency)}
                </span>
              </div>
              {hasRecurringSchedules && overdueSchedules.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  This includes {overdueSchedules.length} overdue payment{overdueSchedules.length > 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Adyen Drop-in */}
      <Card>
        <CardBody>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Payment Method
          </h2>
          {dropinError && (
            <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded mb-4">
              <p className="font-medium">Failed to load payment methods</p>
              <p className="text-sm">{dropinError}</p>
            </div>
          )}
          {(dropinLoading || (sessionLoading && !sessionData)) && !dropinError && (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" />
              <span className="ml-3 text-gray-500">Loading payment methods...</span>
            </div>
          )}
          <div id="dropin-container" />
        </CardBody>
      </Card>

      {/* Security notice */}
      <div className="text-center text-xs text-gray-400">
        <p>Payments are processed securely by Adyen.</p>
        <p>Your card details are encrypted and never stored on our servers.</p>
      </div>
    </div>
  );
}
